/**
 * MONSTER TOWN — THE GAME CLOCK. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * "A DAY IN TOWN" rests on this file. Everything else in the expansion — who is
 * awake, what is out in the grass, which errand is being offered, whether the
 * lamp posts are on — is a question asked of this clock.
 *
 * WHY A GAME CLOCK AND NOT THE REAL ONE
 * A kid who only plays at bedtime would only ever see night. So a full day is
 * ~16 minutes of play (`DEFAULT_DAY_MS`), and SLEEPING IN THE BED is how a kid
 * takes hold of time (`sleepToMorning`). That is the whole control scheme: it is
 * discoverable, it is one tap, and it quietly teaches the shape of a day.
 *
 * WHAT IS DATA AND WHAT IS ENGINE
 * The BANDS (dawn/day/dusk/night — when they start, how dark they are, which
 * palette keys and ambience they carry) are DATA: `setDayPhases(table)`. A new
 * band, or a re-tuned one, is a content drop into `src/content/daytime.ts`. This
 * file only knows how to divide 0..1 and hand you the answer.
 *
 * LAW #2 LIVES HERE TOO. There is no deadline in this file. Nothing expires,
 * nothing is missed, no API can report "too late". `hour` is a number for
 * schedules to read, never a countdown a kid can lose.
 *
 * DETERMINISM. The clock is a pure function of `elapsedMs`. `advance(ms)` is the
 * only way it moves; the engine system just feeds it real frame time. A test (or
 * the harness) calls `advance()` directly and gets bit-identical results.
 */

import './dayEvents'
import { bus } from './events'
import { engine } from './engine'

// ---------------------------------------------------------------------------
// bands — the SHAPE is here, the TABLE is data
// ---------------------------------------------------------------------------

export type TimePhaseId = 'dawn' | 'day' | 'dusk' | 'night'

/**
 * One band of the day. `from` is a day fraction in [0,1); bands are sorted and
 * each runs until the next one starts (the last wraps to the first).
 *
 * Colours are PALETTE KEYS, never hex — a band is a mood change inside the
 * committed palette family, never a different game (expansion brief §1).
 */
export interface TimePhaseBand {
  readonly id: TimePhaseId
  /** Day fraction 0..1 where this band begins. */
  readonly from: number
  /** 0 = broad daylight, 1 = deepest night. Drives lamps, windows, stars. */
  readonly darkness: number
  /** Palette key names — see src/core/palette.ts. */
  readonly skyTop: string
  readonly skyHorizon: string
  readonly sunWarm: string
  readonly ambient: string
  /** Ambient audio bed (audio crew's cue table). */
  readonly ambientCue: string
  /** Icon key for the HUD's little sun/moon dial. Pictures only (LAW #4). */
  readonly icon: string
}

/**
 * The shipped table. Content may replace it wholesale via `setDayPhases`; this
 * default exists so nothing in core ever depends on the content crew having run.
 */
export const DEFAULT_DAY_PHASES: readonly TimePhaseBand[] = [
  // NIGHT WRAPS THROUGH MIDNIGHT, which is why the first band does not start at
  // 0: at 3am a kid is looking at stars, not at a sunrise. `phaseAt` resolves
  // anything before the first band to the LAST one, so 20:00-05:00 is one
  // unbroken night across the day boundary.
  { id: 'dawn',  from:  5 / 24,   darkness: 0.35, skyTop: 'sunWarm',      skyHorizon: 'flowerPink',  sunWarm: 'sunWarm',     ambient: 'ambientCool', ambientCue: 'amb_morning', icon: 'sunrise' },
  { id: 'day',   from:  7 / 24,   darkness: 0.00, skyTop: 'skyTop',       skyHorizon: 'skyHorizon',  sunWarm: 'sunWarm',     ambient: 'sunWarm',     ambientCue: 'amb_day',     icon: 'sun' },
  { id: 'dusk',  from: 17.5 / 24, darkness: 0.40, skyTop: 'monsterLilac', skyHorizon: 'uiCoin',      sunWarm: 'roofMustard', ambient: 'ambientCool', ambientCue: 'amb_evening', icon: 'sunset' },
  { id: 'night', from: 20 / 24,   darkness: 1.00, skyTop: 'shadow',       skyHorizon: 'ambientCool', sunWarm: 'ambientCool', ambient: 'shadow',      ambientCue: 'amb_night',   icon: 'moon' },
]

/** Real milliseconds for one full in-world day. Brief: 15–18 minutes. */
export const DEFAULT_DAY_MS = 16 * 60_000
/** The hour `sleepToMorning` lands on, and the hour a fresh town starts at. */
export const MORNING_HOUR = 7

// ---------------------------------------------------------------------------
// snapshot
// ---------------------------------------------------------------------------

export interface TimeSnapshot {
  /** 1-based in-world day. Carries across sessions; purely cosmetic (LAW #2). */
  readonly day: number
  /** Position through the day, 0..1. */
  readonly t: number
  /** Same thing as a 24-hour clock, for schedules and for dev eyes. */
  readonly hour: number
  readonly phase: TimePhaseId
  /** 0..1 through the CURRENT band. */
  readonly phaseT: number
  /** 0..1, smoothly interpolated ACROSS band edges. Never steps. */
  readonly darkness: number
  readonly paused: boolean
}

export interface TimeSaveState {
  day: number
  t: number
}

export interface PhaseChange {
  readonly phase: TimePhaseId
  readonly previous: TimePhaseId
  readonly day: number
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const wrap01 = (v: number): number => {
  const w = v % 1
  return w < 0 ? w + 1 : w
}

/** Hour (0..24, may be fractional) -> day fraction. */
export const hourToT = (hour: number): number => wrap01(hour / 24)
/** Day fraction -> hour (0..24). */
export const tToHour = (t: number): number => wrap01(t) * 24

// ---------------------------------------------------------------------------
// the clock
// ---------------------------------------------------------------------------

export class GameClock {
  private bands: readonly TimePhaseBand[] = DEFAULT_DAY_PHASES
  private dayMs = DEFAULT_DAY_MS
  private frac = hourToT(MORNING_HOUR)
  private dayNo = 1
  /** Ref-counted, keyed by reason so two systems pausing is safe. */
  private pauses = new Set<string>()
  private lastPhase: TimePhaseId = 'day'  // recomputed on first setDayPhases/restore
  private tickFns = new Set<(s: TimeSnapshot) => void>()
  private phaseFns = new Set<(c: PhaseChange) => void>()
  private alarms: Array<{ id: number; at: number; fn: (s: TimeSnapshot) => void; repeat: boolean; armedDay: number }> = []
  private nextAlarmId = 1

  // -- configuration (data drops call these) ------------------------------

  /** Replace the band table. Sorted and validated; a bad table is ignored. */
  setDayPhases(bands: readonly TimePhaseBand[]): boolean {
    if (bands.length === 0) return false
    // A table need NOT start at 0 — the last band wraps through midnight, which
    // is the only way "night" is one thing rather than two.
    const sorted = [...bands].sort((a, b) => a.from - b.from)
    if (sorted.some((b) => !Number.isFinite(b.from) || b.from < 0 || b.from >= 1)) return false
    this.bands = sorted
    this.lastPhase = this.phaseAt(this.frac).id
    return true
  }

  dayPhases(): readonly TimePhaseBand[] {
    return this.bands
  }

  /** Real ms per in-world day. Brief says 15–18 min; the harness may go faster. */
  setDayLengthMs(ms: number): void {
    this.dayMs = Math.max(1000, ms)
  }

  get dayLengthMs(): number {
    return this.dayMs
  }

  // -- reading -------------------------------------------------------------

  get t(): number {
    return this.frac
  }
  get day(): number {
    return this.dayNo
  }
  get hour(): number {
    return tToHour(this.frac)
  }
  get phase(): TimePhaseId {
    return this.phaseAt(this.frac).id
  }
  get paused(): boolean {
    return this.pauses.size > 0
  }

  /** The band covering a day fraction. Bands wrap; this always returns one. */
  phaseAt(t: number): TimePhaseBand {
    const f = wrap01(t)
    let found = this.bands[this.bands.length - 1]!
    for (const b of this.bands) {
      if (f >= b.from) found = b
      else break
    }
    return found
  }

  private bandSpan(b: TimePhaseBand): { from: number; to: number } {
    const i = this.bands.indexOf(b)
    const next = this.bands[(i + 1) % this.bands.length]!
    const to = next.from <= b.from ? next.from + 1 : next.from
    return { from: b.from, to }
  }

  /**
   * Darkness, interpolated across band edges so the world eases into night
   * instead of snapping. Pure; safe to call every frame.
   */
  darknessAt(t: number): number {
    const f = wrap01(t)
    const b = this.phaseAt(f)
    const { from, to } = this.bandSpan(b)
    const span = to - from
    if (span <= 0) return b.darkness
    const local = (f >= from ? f - from : f + 1 - from) / span
    const i = this.bands.indexOf(b)
    const next = this.bands[(i + 1) % this.bands.length]!
    // Ease over the last third of a band into the next one's level.
    const blend = local <= 0.66 ? 0 : (local - 0.66) / 0.34
    const s = blend * blend * (3 - 2 * blend)
    return b.darkness + (next.darkness - b.darkness) * s
  }

  now(): TimeSnapshot {
    const b = this.phaseAt(this.frac)
    const { from, to } = this.bandSpan(b)
    const span = to - from || 1
    const local = (this.frac >= from ? this.frac - from : this.frac + 1 - from) / span
    return {
      day: this.dayNo,
      t: this.frac,
      hour: tToHour(this.frac),
      phase: b.id,
      phaseT: Math.max(0, Math.min(1, local)),
      darkness: this.darknessAt(this.frac),
      paused: this.paused,
    }
  }

  // -- moving --------------------------------------------------------------

  /**
   * THE ONLY WAY TIME MOVES. Deterministic: same ms in, same state out. The
   * engine system feeds it frame time; tests call it directly.
   *
   * Rolls the day counter and fires phase changes and alarms in order, so a
   * single huge `advance()` behaves exactly like many small ones.
   */
  advance(ms: number): TimeSnapshot {
    if (ms <= 0 || this.paused) return this.now()
    let remaining = ms
    // Step in at most quarter-day slices so nothing between here and there is
    // skipped, however big the jump (a sleep is a jump).
    const slice = this.dayMs / 4
    let guard = 0
    while (remaining > 0 && guard++ < 4096) {
      const step = Math.min(remaining, slice)
      remaining -= step
      this.setFraction(this.frac + step / this.dayMs, false)
    }
    return this.now()
  }

  /**
   * Jump to a fraction of the day. `crossedDay` is inferred: going backwards
   * past 0 counts as the next day, which is what sleeping does.
   */
  /**
   * `naturalRoll` is what tells a WRAP from a JUMP. Time flowing past midnight
   * is a new day; a tool (or a save hydrate) setting the clock to 3am is not —
   * that used to tick the day counter every time a test looked at the morning.
   */
  private setFraction(next: number, forceNewDay: boolean, naturalRoll = true): void {
    const prevFrac = this.frac
    const wrapped = wrap01(next)
    const rolled = forceNewDay || (naturalRoll && (next >= 1 || wrapped < prevFrac))
    this.frac = wrapped
    if (rolled) {
      this.dayNo++
      bus.emit('time:dayStarted', { day: this.dayNo })
    }
    const phase = this.phaseAt(this.frac).id
    if (phase !== this.lastPhase) {
      const change: PhaseChange = { phase, previous: this.lastPhase, day: this.dayNo }
      this.lastPhase = phase
      for (const fn of [...this.phaseFns]) safe(() => fn(change))
      bus.emit('time:phaseChanged', change)
    }
    this.fireAlarms(prevFrac, this.frac, rolled)
    const snap = this.now()
    for (const fn of [...this.tickFns]) safe(() => fn(snap))
    bus.emit('time:tick', snap)
  }

  /**
   * Set the clock to an hour. Used by `sleepToMorning`, by the harness, and by
   * save hydration. Never emits a "you missed it" — there is no such thing.
   */
  setHour(hour: number, opts: { nextDay?: boolean } = {}): TimeSnapshot {
    // A JUMP, not a wrap: setting the clock back to 3am does not make it
    // tomorrow. Pass `nextDay` when you mean it (the bed does).
    this.setFraction(hourToT(hour), opts.nextDay === true, false)
    return this.now()
  }

  setDay(day: number): void {
    this.dayNo = Math.max(1, Math.round(day))
  }

  /**
   * THE BED. Always lands on the SAME morning hour, always on the NEXT day, from
   * any time of day — including 3am, which is when a kid would otherwise sleep
   * "backwards" into the same morning they came from.
   *
   * Synchronous and deterministic; the fade is the HUD crew's, driven off
   * `time:slept`.
   */
  sleepToMorning(): TimeSnapshot {
    const from = this.now()
    // Before the morning hour it is still "tonight", so we roll the day exactly
    // once either way.
    this.setFraction(hourToT(MORNING_HOUR), true)
    const to = this.now()
    bus.emit('time:slept', { fromHour: from.hour, toHour: to.hour, day: to.day })
    return to
  }

  // -- pausing -------------------------------------------------------------

  /**
   * Ref-counted by REASON. Two systems pausing for two reasons both have to let
   * go before the world moves again, and a system that pauses twice for the same
   * reason has not broken anything.
   */
  pause(reason: string): void {
    this.pauses.add(reason)
  }
  resume(reason: string): void {
    this.pauses.delete(reason)
  }
  pauseReasons(): string[] {
    return [...this.pauses]
  }

  // -- subscribing ---------------------------------------------------------

  /** Every clock step. Cheap — do not do work here, read `now()` instead. */
  onTick(fn: (s: TimeSnapshot) => void): () => void {
    this.tickFns.add(fn)
    return () => this.tickFns.delete(fn)
  }

  onPhase(fn: (c: PhaseChange) => void): () => void {
    this.phaseFns.add(fn)
    return () => this.phaseFns.delete(fn)
  }

  /**
   * "Call me at 8 o'clock." Fires when the clock CROSSES that hour, including
   * across a sleep jump. `repeat: true` re-arms every day.
   *
   * This is not a deadline. Missing an alarm costs nothing and cannot happen —
   * a jump that steps over the hour still fires it.
   */
  at(hour: number, fn: (s: TimeSnapshot) => void, opts: { repeat?: boolean } = {}): () => void {
    const id = this.nextAlarmId++
    this.alarms.push({ id, at: hourToT(hour), fn, repeat: opts.repeat === true, armedDay: 0 })
    return () => {
      const i = this.alarms.findIndex((a) => a.id === id)
      if (i >= 0) this.alarms.splice(i, 1)
    }
  }

  private fireAlarms(from: number, to: number, rolled: boolean): void {
    const crossed = (mark: number): boolean =>
      rolled || from === to ? true : from < to ? mark > from && mark <= to : mark > from || mark <= to
    for (const a of [...this.alarms]) {
      if (a.armedDay === this.dayNo) continue
      if (!crossed(a.at)) continue
      a.armedDay = this.dayNo
      const snap = this.now()
      safe(() => a.fn(snap))
      if (!a.repeat) {
        const i = this.alarms.indexOf(a)
        if (i >= 0) this.alarms.splice(i, 1)
      }
    }
  }

  // -- persistence ---------------------------------------------------------

  snapshot(): TimeSaveState {
    return { day: this.dayNo, t: this.frac }
  }

  /** Never throws, never rejects. A junk clock is a morning. */
  restore(state: TimeSaveState): void {
    const day = Number.isFinite(state.day) ? Math.max(1, Math.round(state.day)) : 1
    const t = Number.isFinite(state.t) ? wrap01(state.t) : hourToT(MORNING_HOUR)
    this.dayNo = day
    this.frac = t
    this.lastPhase = this.phaseAt(t).id
    for (const a of this.alarms) a.armedDay = 0
  }

  /** Test hook: forget every listener and alarm. Used by unit-style checks. */
  resetForTest(): void {
    this.tickFns.clear()
    this.phaseFns.clear()
    this.alarms.length = 0
    this.pauses.clear()
    this.dayMs = DEFAULT_DAY_MS
    this.dayNo = 1
    this.frac = hourToT(MORNING_HOUR)
    this.lastPhase = this.phaseAt(this.frac).id
  }
}

function safe(fn: () => void): void {
  try {
    fn()
  } catch (err) {
    // A broken listener never takes down a game a kid is in the middle of.
    console.error('[time] listener threw', err)
  }
}

/** The one clock. Import this; do not construct another. */
export const clock = new GameClock()

// ---------------------------------------------------------------------------
// engine wiring
// ---------------------------------------------------------------------------

/**
 * ORDER 1, inside the 0–9 input/camera band and deliberately ahead of it:
 * everything that runs later this frame — camera tint, NPC routines, spawns —
 * must see one consistent answer to "what time is it".
 *
 * Called once from `main.ts`. Idempotent.
 */
let installed = false
export function installClock(): void {
  if (installed) return
  installed = true
  engine.addSystem({
    id: 'core.time',
    order: 1,
    fixed: (dt) => clock.advance(dt * 1000),
  })
  // A takeover screen is not play. Nothing is lost either way (LAW #2) — this
  // just means a kid who opened the creator does not come back to nightfall.
  bus.on('app:phaseChanged', (e) => {
    const freeze = e.phase === 'monsterCreator' || e.phase === 'slotPick' || e.phase === 'photo'
    if (freeze) clock.pause('phase')
    else clock.resume('phase')
  })
  bus.on('scene:transitionStart', () => clock.pause('transition'))
  bus.on('scene:transitionEnd', () => clock.resume('transition'))
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') clock.pause('hidden')
      else clock.resume('hidden')
    })
  }
}
